<template>
    <div>
        <user-tour-onboarding
            v-if="activeTour && activeTour.tour.identifier !== 'guided_tour_dashboard'"
            :active-tour="activeTour"
            @close-tour="complete"
        />

        <div v-if="hasTours && activeTour && activeTour.tour.identifier === 'guided_tour_dashboard'">
            <e-modal
                :model-value="Boolean(activeTourStep)"
                :can-cancel="false"
                :destroy-on-hide="false"
                has-modal-card
                aria-role="dialog"
                close-button-aria-label="Close"
                trap-focus
            >
                <template #default>
                    <component
                        :is="tourStepComponent"
                        @back="goToStep(-1)"
                        @next="goToStep(1)"
                        @finish="complete"
                    />
                </template>
            </e-modal>
        </div>
    </div>
</template>

<script>
import DashboardGuidedTourStepOne from '@/components/dashboard/guided-tour/DashboardGuidedTourStepOne.vue';
import DashboardGuidedTourStepThree from '@/components/dashboard/guided-tour/DashboardGuidedTourStepThree.vue';
import DashboardGuidedTourStepTwo from '@/components/dashboard/guided-tour/DashboardGuidedTourStepTwo.vue';
import UserTourOnboarding from '@/components/UserTourOnboarding.vue';
import CourseEnrollment from '@/models/CourseEnrollment.js';

/**
 * This component is currently only rendering the Dashboard tour. It needs to be refactored to support tours
 * again when they're needed.
 */
const DASHBOARD_GUIDED_TOUR_STEPS = {
    1: DashboardGuidedTourStepOne,
    2: DashboardGuidedTourStepTwo,
    3: DashboardGuidedTourStepThree,
};

export default {
    name: 'UserTour',

    components: {
        UserTourOnboarding,
        DashboardGuidedTourStepOne,
        DashboardGuidedTourStepTwo,
        DashboardGuidedTourStepThree,
    },

    data: () => ({
        activeTourStep: null,
        openDrawer: false,
        hasTours: false,
    }),

    computed: {
        /**
         * @returns {*}
         */
        activeTour() {
            return this.$store.getters['Tours/firstIncompleteTour']();
        },

        /**
         * @returns {string}
         */
        tourName() {
            return this.$store.getters['Tours/tourName'](this.activeTour);
        },

        /**
         * @returns {*[]}
         */
        tourSteps() {
            return this.$store.getters['Tours/tourSteps'](this.activeTour);
        },

        /**
         * @returns {*}
         */
        tourOptions() {
            return {
                startTimeout: 2000,
                labels: {
                    buttonSkip: 'Skip',
                    buttonPrevious: 'Previous',
                    buttonNext: 'Next',
                    buttonStop: 'Finish',
                },
            };
        },

        /**
         * @returns {*}
         */
        tourCallbacks() {
            return {
                onSkip: () => this.$store.dispatch('Tours/skip', this.activeTour),
                onFinish: () => this.$store.dispatch('Tours/complete', this.activeTour),
            };
        },

        /**
         * @returns {*}
         */
        tourStepComponent() {
            return DASHBOARD_GUIDED_TOUR_STEPS[this.activeTourStep];
        },

        /**
         * @returns {Array}
         */
        enrolledCourses() {
            if (!this.$store.state.Auth.user) {
                return [];
            }

            return (
                CourseEnrollment.query()
                    .with('course.sections.time_periods')
                    .where('user_id', this.$store.state.Auth.user.id)
                    .get() || []
            );
        },
    },

    async created() {
        await this.$store.dispatch('Tours/fetchIncompleteToursForLocation', 'home');

        this.hasTours = this.$store.getters['Tours/hasTours']();

        if (
            this.enrolledCourses.length &&
            this.activeTour &&
            this.activeTour.tour.identifier === 'guided_tour_dashboard'
        ) {
            // If the user is logged in, has courses, has an active tour,
            // and the active tour is for the Dashboard.
            this.activeTourStep = 1;
        }
    },

    methods: {
        /**
         * @param stepIncrement
         */
        goToStep(stepIncrement) {
            this.activeTourStep += stepIncrement;
        },

        complete() {
            this.$store.dispatch('Tours/complete', this.activeTour);

            this.hasTours = false;
            this.activeTourStep = null;

            this.$nextTick(() => {
                this.openDashboardConfigurationDrawer();
            });
        },

        openDashboardConfigurationDrawer() {
            return this.$store.commit('SET_WIDGET_DRAWER', { state: true });
        },
    },
};
</script>

<style lang="scss">
@import '~assets/scss/toolbox.scss';

.dashboardGuidedTour {
    &.card {
        margin: 0;

        .card-content {
            border-top: 0;
        }
    }

    &__description,
    &__layoutOptions {
        margin: 0 auto;
        max-width: 550px;
    }

    h1 {
        &:after {
            display: none !important;
        }

        span {
            position: relative;

            &::after {
                background: var(--primary-light);
                content: '';
                display: block;
                height: 4px;
                left: 8px;
                margin-bottom: calc($size-10 / 2);
                margin-top: calc($size-10 / 2);
                position: absolute;
                width: 5rem;
            }
        }
    }
}
</style>
